<template>
  <div>
    <div class="row">
      <div class="col-sm-12">
        <h1>
          Key Vet Dairy Winter Consult {{ seasonStartYear }}
          <span v-if="currentClientName">- {{ currentClientName }}</span>
        </h1>
      </div>
    </div>

    <loader v-if="isLoadingApi"></loader>

    <template v-if="dataFetched">
      <consult-opened-by-other-warning></consult-opened-by-other-warning>
      <consult-backend-errors store-namespace="currentConsult"></consult-backend-errors>

      <consult-navigation></consult-navigation>

      <div class="alert alert-danger" v-if="dataFetched && getFieldValue('state', 'Misc') == 'saving'">
        This consult encountered errors when submitting, it is saved but needs fixing. Please contact an administrator.
      </div>

      <div class="row">
        <div :class="[showVetNotes ? 'col-xs-9' : 'col-xs-12']">
          <vet-notes-toggle style="position: absolute; top: 30px; right: 20px"> </vet-notes-toggle>
          <component :is="currentComponent" :key="currentComponent"> </component>
        </div>

        <div class="col-xs-3" v-show="showVetNotes" style="position: sticky; top: 200px; z-index: 3">
          <div style="display: flex; flex-direction: column">
            <vet-notes field-name="vet_notes" model-name="ClientDetails" email-notification="wc-vet-notes"
              v-show="showVetNotes">
            </vet-notes>
            <br />
            <br />
            <farm-services-notes field-name="notes_for_farm_services" model-name="ClientDetails"
              email-notification="notes-for-farm-services">
            </farm-services-notes>
          </div>
        </div>
      </div>

      <consult-navigation v-on:step-changed="scroll()" placement="top"></consult-navigation>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { winterConsultComponents } from '@/components/winter_consult'

import ConsultBackendErrors from '@/components/ConsultBackendErrors'
import ConsultNavigation from '@/components/ConsultNavigation'
import ConsultOpenedByOtherWarning from '@/components/ConsultOpenedByOtherWarning'
import VetNotes from '@/components/VetNotes'
import VetNotesToggle from '@/components/VetNotesToggle'
import FarmServicesNotes from '@/components/FarmServicesNotes'
import currentConsult from '@/_store/current_consult.module'
import winterConsult from '@/_store/winter_consult.module'
import { scrollToTop } from '@/_helpers/utils'
import _ from 'lodash';

export default {
  name: 'WinterConsultPage',
  components: {
    ...winterConsultComponents,
    ConsultBackendErrors,
    ConsultNavigation,
    ConsultOpenedByOtherWarning,
    FarmServicesNotes,
    VetNotes,
    VetNotesToggle,
  },
  data() {
    return {
      dataFetched: false,
      isLoadingApi: false,
    }
  },
  created() {
    // TODO: create consult page mixin
    Object.assign(currentConsult.state, winterConsult.stateMixin)
    Object.assign(currentConsult.actions, winterConsult.actionsMixin)
    Object.assign(currentConsult.mutations, winterConsult.mutationsMixin)
    Object.assign(currentConsult.getters, winterConsult.gettersMixin)

    if (this.$store.hasModule('currentConsult')) {
      this.$store.unregisterModule('currentConsult')
    }

    this.$store.registerModule('currentConsult', currentConsult)
    this.setClient(this.$route.params.client_id)
    this.setCurrentConsultType('winterConsult')
  },
  beforeDestroy() {
    if (this.dataFetched) {
      this.backupState()
    }
  },
  mounted() {
    this.setClient(this.$route.params.client_id)
    let readOnly = Boolean(this.$route.params.history)
    this.fetchData(readOnly)
  },
  watch: {
    '$store.state': {
    handler: _.debounce(function () {
        this.backupState()
    }, 2000),
    deep: true
    }
  },
  methods: {
    ...mapActions('currentConsult', [
      'backupState',
      'getConsultData',
      'getReadOnlyData',
      'removeRequiredFields',
      'setClient',
      'setFieldValue',
    ]),
    ...mapMutations('currentConsult', {
      setCurrentConsultType: 'setConsultType',
      updateWCMarker: 'updateWCMarker',
    }),
    scroll() {
      scrollToTop()
    },
    fetchData(readOnly) {
      this.isLoadingApi = true

      let consultParams = {
        clientId: this.$route.params.client_id,
        season: this.$route.params.season,
      }

      let action = readOnly ? this.getReadOnlyData : this.getConsultData

      return action(consultParams)
        .then(() => {
          this.dataFetched = true
        })
        .catch((error) => {
          if (error.response.data['no_active_farms']) {
            this.$store.dispatch('alert/error', {
              message:
                'The dairy consult could not be created because this client does not have a valid dairy shed herd registered in KVC.  Please contact farm services to resolve this issue.',
              persist: true,
            })
          } else {
            this.$store.dispatch('alert/error', {
              message: 'An error occurred. Contact the administrator.',
              persist: true,
            })
          }
          this.$router.push('/')
        })
        .finally(() => {
          this.isLoadingApi = false
        })
    },
  },
  computed: {
    ...mapGetters('currentConsult', [
      'consultSteps',
      'currentClientName',
      'currentComponent',
      'getFieldValue',
      'seasonStartYear',
      'showVetNotes',
    ]),
  },
}
</script>

<style>
.farm-fields-xl {
  width: 500px;
}
</style>
